import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { colors } from '../theme/theme';

export const filterToggleSx = (selected: boolean) => ({
  flex: '0 1 auto',
  textTransform: 'none' as const,
  fontWeight: 600,
  fontSize: '0.8125rem',
  px: 1.5,
  py: 0.75,
  borderColor: selected ? colors.primary : colors.divider,
  color: selected ? colors.white : colors.textSecondary,
  bgcolor: selected ? colors.primary : 'transparent',
  '&:hover': {
    borderColor: colors.primary,
    bgcolor: selected ? colors.primary : colors.backgroundTertiary,
    color: selected ? colors.white : colors.textSecondary,
  },
  '&.Mui-disabled': {
    borderColor: colors.divider,
    color: colors.textSecondary,
    bgcolor: 'transparent',
    opacity: 0.45,
  },
});

export type FilterOption = { value: string; label: string };

export interface SearchableMultiSelectOption {
  _id: string;
  studioName: string;
  city?: string;
}

export type AdvancedFilterSection =
  | {
      type: 'single-select-toggle';
      label: string;
      options: FilterOption[];
      value: string | null;
      onToggle: (value: string) => void;
      rows?: FilterOption[][];
      visible?: boolean;
    }
  | {
      type: 'multi-select-pills';
      label: string;
      options: string[];
      selected: string[];
      onToggle: (value: string) => void;
      emptyText?: string;
      maxHeight?: number;
      visible?: boolean;
    }
  | {
      type: 'searchable-multi-select';
      label: string;
      options: SearchableMultiSelectOption[];
      selectedIds: string[];
      onChange: (ids: string[]) => void;
      loading?: boolean;
      visible?: boolean;
    }
  | {
      type: 'compound-sort';
      sortByLabel?: string;
      sortByOptions: FilterOption[];
      sortBy: string | null;
      sortDir: 'desc' | 'asc' | null;
      onSortByToggle: (value: string) => void;
      onSortDirToggle: (value: string) => void;
      sortDirOptions: FilterOption[];
      visible?: boolean;
    };

interface AdvancedFiltersPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  sections: AdvancedFilterSection[];
  onApply: () => void;
  onClearAll: () => void;
  width?: number;
}

function renderToggleButtons(
  options: FilterOption[],
  isSelected: (value: string) => boolean,
  onToggle: (value: string) => void,
  mb = 2,
  disabled = false,
) {
  return (
    <Box display="flex" gap={1} flexWrap="wrap" mb={mb}>
      {options.map(option => (
        <Button
          key={option.value}
          variant="outlined"
          size="small"
          disabled={disabled}
          onClick={() => onToggle(option.value)}
          sx={filterToggleSx(isSelected(option.value))}
        >
          {option.label}
        </Button>
      ))}
    </Box>
  );
}

const AdvancedFiltersPopover: React.FC<AdvancedFiltersPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  sections,
  onApply,
  onClearAll,
  width = 360,
}) => {
  const visibleSections = sections.filter(section => section.visible !== false);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      slotProps={{ paper: { sx: { mt: 1, borderRadius: 2, width, maxWidth: '95vw' } } }}
    >
      <Box sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={700} mb={2}>
          Advanced Filters
        </Typography>

        {visibleSections.map((section, index) => {
          const isLast = index === visibleSections.length - 1;
          const sectionMb = isLast ? 2.5 : 0;

          if (section.type === 'single-select-toggle') {
            return (
              <Box key={`${section.label}-${index}`} mb={sectionMb}>
                <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={1}>
                  {section.label}
                </Typography>
                {section.rows
                  ? section.rows.map((row, rowIndex) =>
                      renderToggleButtons(
                        row,
                        value => section.value === value,
                        section.onToggle,
                        rowIndex === section.rows!.length - 1 ? 2 : 1,
                      ),
                    )
                  : renderToggleButtons(
                      section.options,
                      value => section.value === value,
                      section.onToggle,
                      2,
                    )}
              </Box>
            );
          }

          if (section.type === 'multi-select-pills') {
            return (
              <Box key={`${section.label}-${index}`} mb={sectionMb}>
                <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={1}>
                  {section.label}
                </Typography>
                <Box
                  display="flex"
                  gap={0.75}
                  flexWrap="wrap"
                  mb={2.5}
                  maxHeight={section.maxHeight ?? 160}
                  sx={{ overflowY: 'auto' }}
                >
                  {section.options.length === 0 ? (
                    <Typography variant="caption" color="text.secondary">
                      {section.emptyText ?? 'No options available'}
                    </Typography>
                  ) : (
                    section.options.map(option => {
                      const selected = section.selected.includes(option);
                      return (
                        <Chip
                          key={option}
                          label={option}
                          size="small"
                          onClick={() => section.onToggle(option)}
                          sx={{
                            fontWeight: 600,
                            cursor: 'pointer',
                            bgcolor: selected ? colors.primary : 'transparent',
                            color: selected ? colors.white : colors.textSecondary,
                            border: '1px solid',
                            borderColor: selected ? colors.primary : colors.divider,
                            '&:hover': {
                              borderColor: colors.primary,
                              bgcolor: selected ? colors.primary : colors.backgroundTertiary,
                            },
                          }}
                        />
                      );
                    })
                  )}
                </Box>
              </Box>
            );
          }

          if (section.type === 'searchable-multi-select') {
            const selectedOptions = section.options.filter(option =>
              section.selectedIds.includes(option._id),
            );

            return (
              <Box key={`${section.label}-${index}`} mb={2}>
                <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={1}>
                  {section.label}
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={section.options}
                  loading={section.loading}
                  value={selectedOptions}
                  getOptionLabel={option => option.studioName || ''}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                  onChange={(_, newValue) => {
                    section.onChange(newValue.map(option => option._id));
                  }}
                  filterOptions={(options, { inputValue }) =>
                    options.filter(
                      option =>
                        option.studioName?.toLowerCase().includes(inputValue.toLowerCase()) ||
                        option.city?.toLowerCase().includes(inputValue.toLowerCase()),
                    )
                  }
                  renderInput={params => (
                    <TextField {...params} placeholder="Search studios..." variant="outlined" />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option._id}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {option.studioName}
                        </Typography>
                        {option.city && (
                          <Typography variant="caption" color="text.secondary">
                            {option.city}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                  noOptionsText="No studios found"
                />
              </Box>
            );
          }

          if (section.type === 'compound-sort') {
            return (
              <Box key={`compound-sort-${index}`} mb={sectionMb}>
                <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" mb={1}>
                  {section.sortByLabel ?? 'Sort by'}
                </Typography>
                {renderToggleButtons(
                  section.sortByOptions,
                  value => section.sortBy === value,
                  section.onSortByToggle,
                  1,
                )}
                {renderToggleButtons(
                  section.sortDirOptions,
                  value => section.sortDir === value,
                  section.onSortDirToggle,
                  2.5,
                  !section.sortBy,
                )}
              </Box>
            );
          }

          return null;
        })}

        <Box display="flex" gap={1} justifyContent="flex-end">
          <Button variant="text" onClick={onClearAll} sx={{ textTransform: 'none' }}>
            Clear all
          </Button>
          <Button variant="contained" onClick={onApply} sx={{ textTransform: 'none' }}>
            Apply
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};

export default AdvancedFiltersPopover;
